/**
 * SimEvent → audio cues (pure). One event can produce SFX, an announcer line candidate, a crowd reaction and a
 * "heat" contribution (HE'S ON FIRE tracking). The bridge collects the cues of one frame into a batch and
 * arbitrates: one result stinger, the best announcer line and the strongest crowd reaction per frame.
 *
 * Sound ids are @hf/audio SFX_BANK ids; line ids are announcer asset ids (public/audio/audio-manifest.json).
 */
import type { CrowdReaction } from '@hf/audio';
import { CHARACTER_IDS, SpecialId, yardsToGoal } from '@hf/shared';
import {
  ConversionChoice,
  PlayCall,
  PlayResultKind,
  PlayerState,
  type SimEvent,
  type SimState,
} from '@hf/sim';
import { panFor } from './crowd-level';

export interface SfxCue {
  id: string;
  volume?: number;
  pitch?: number;
  pan?: number;
  /** Plays even when the event arrives late (rollback / long frame). Movement noise is dropped when stale. */
  urgent?: boolean;
}

export interface LineCue {
  /** Announcer line id, or a variant group (`LINE_VARIANTS`): the bridge rotates its takes. */
  id: string;
  priority: number;
  /** Probability gate for colour lines (default 1). */
  chance?: number;
  /** Minimum time between two plays of this line (default 5 s). */
  cooldownMs?: number;
  /** Colour commentary (dropped in tutorials, rate-limited). */
  chatter?: boolean;
  /** Belongs to the line already speaking (queues behind it however long it is; AnnounceOptions.chain). */
  chain?: boolean;
  /** Said at most once per match (the opening kickoff, "SECOND HALF!", half-time). */
  once?: boolean;
  /**
   * When a bigger call wins the frame, this one is chained right behind it instead of being lost ("SACKED!!" then
   * "Turnover on downs!": the 4th-down sack's change of possession).
   */
  follow?: boolean;
  /**
   * Unchained: how long (s) it may wait for the booth (AnnounceOptions.wait). Default: `CALL_WAIT_S` for a call
   * (priority ≥ 2), the booth's 1.6 s for colour.
   */
  wait?: number;
  /**
   * Not news mid-play (a meter filling on the way to a score): held until the ball is dead and said when the booth
   * is free between plays — where the special callout's pre-snap warning ("watch for the BLITZ!") belongs anyway.
   */
  deadBall?: boolean;
}

/** Takes of the long-score replay narration ("He's at the forty… the twenty…", "Look at him GO…", "He breaks free…"). */
export const LONG_TD_TAKES: readonly string[] = ['touchdown_alt', 'touchdown_alt_2', 'touchdown_alt_3'];

/**
 * Variant groups: a call that comes up often has several takes, and the bridge picks the one heard least
 * recently (a variant missing from the manifest is skipped), so the booth never sounds like a loop.
 */
export const LINE_VARIANTS: Readonly<Record<string, readonly string[]>> = {
  touchdown: ['touchdown', 'touchdown_2'],
  first_down: ['first_down', 'first_down_2'],
  what_a_fake: ['what_a_fake_2', 'what_a_fake'],
  big_hit: ['big_hit', 'big_hit_2'],
  sacked: ['sacked', 'sacked_2'],
  incomplete: ['incomplete', 'incomplete_2'],
  picked_off: ['picked_off', 'picked_off_2'],
  replay: ['replay', 'replay_2'],
  // the long-score narration of an instant replay (in a match, and the Results reel with its own cooldown)
  long_td: LONG_TD_TAKES,
  long_td_reel: LONG_TD_TAKES,
  // the short take first: a spin is a mid-play bark, and the long read (4.9 s) outlasts the play
  spin_move: ['spin_move_2', 'spin_move'],
  no_way: ['no_way_2', 'no_way'],
};

/** Every announcer line id the bridge can ask for (variants included; the manifest test checks them all). */
export const ANNOUNCER_LINES: readonly string[] = [
  // screens
  'title',
  'choose_your_head',
  'versus',
  ...CHARACTER_IDS,
  'winner',
  // match flow
  'kickoff',
  'fourth_down',
  'offside',
  'fg_attempt',
  'two_point',
  'overtime',
  'ballgame',
  // plays
  'touchdown',
  'touchdown_2',
  ...LONG_TD_TAKES,
  'pick_six',
  'return_td',
  'what_a_fake',
  'what_a_fake_2',
  'picked_off',
  'picked_off_2',
  'sacked',
  'sacked_2',
  'first_down',
  'first_down_2',
  'look_at_him_go',
  'what_a_catch',
  'incomplete',
  'incomplete_2',
  'swatted',
  'big_hit',
  'big_hit_2',
  'denied',
  'right_through_him',
  'over_the_top',
  'spin_move',
  'spin_move_2',
  'no_way',
  'no_way_2',
  'safety',
  'turnover_on_downs',
  'doink',
  // kicking & conversions
  'its_good',
  'its_good_long',
  'no_good',
  'pat_good',
  'two_good',
  'two_stopped',
  // Classic format
  'kick_away',
  'onside_kick',
  'onside_recovered',
  'recovered',
  'punt',
  'downed',
  'touchback',
  'take_a_knee',
  'halftime',
  'second_half',
  'thirty_seconds',
  'thirty_seconds_game',
  // streaks, stamina, specials
  'heating_up',
  'on_fire',
  'gassed',
  'hail_mary',
  'bulldozer',
  'mirage',
  'blitz',
  'ready_rookie',
  'ready_bigtex',
  'ready_zorg',
  'ready_grandpa',
  // instant replays
  'replay',
  'replay_2',
];

/**
 * A score this long (yards) earns the long replay narration: "He's at the forty… the twenty…" must be true (a 30-yard
 * score never crossed the forty).
 */
export const LONG_TD_YARDS = 40;
/** A field goal toss from at least this far is called "From WAY downtown…". */
export const LONG_FG_YARDS = 40;

/** Line announcing that a character's special is charged (POWER_FULL), by character id. */
export const READY_LINES: Readonly<Record<string, string>> = {
  rookie: 'ready_rookie',
  bigtex: 'ready_bigtex',
  zorg: 'ready_zorg',
  grandpa: 'ready_grandpa',
};

export interface CrowdCue {
  kind: CrowdReaction;
  strength: number;
}

export interface HeatCue {
  player: number;
  amount: number;
}

export interface CueBatch {
  sfx: SfxCue[];
  lines: LineCue[];
  crowd: CrowdCue[];
  heat: HeatCue[];
  /** A play/possession reset happened (heat of the other side cools). */
  coolOpponentOf: number[];
}

export const createBatch = (): CueBatch => ({ sfx: [], lines: [], crowd: [], heat: [], coolOpponentOf: [] });
export const clearBatch = (b: CueBatch): void => {
  b.sfx.length = 0;
  b.lines.length = 0;
  b.crowd.length = 0;
  b.heat.length = 0;
  b.coolOpponentOf.length = 0;
};

/**
 * Result stingers: only the highest-ranked one sounds per frame (e.g. a touchdown also emits BIG_PLAY/FIRST_DOWN,
 * a PAT emits both FIELD_GOAL_GOOD and CONVERSION_RESULT).
 */
export const STINGER_RANK: Readonly<Record<string, number>> = {
  final_horn: 11,
  td_fanfare: 10,
  overtime: 9,
  safety: 8,
  fg_good: 7,
  fg_miss: 6,
  conversion_good: 5,
  conversion_fail: 4,
  sack: 3,
  turnover: 2,
  first_down: 1,
};

/** Announcer id for a character index (SimState stores indices). */
export const characterLine = (index: number): string | null => CHARACTER_IDS[index] ?? null;

/** Special-move callouts: above every highlight (2), under the results (3) they set up. */
export const SPECIAL_PRIORITY = 2.8;

const SPECIAL_LINES: Readonly<Record<number, { sfx: string; line: string }>> = {
  [SpecialId.HAIL_MARY]: { sfx: 'special_hail_mary', line: 'hail_mary' },
  [SpecialId.BULLDOZER]: { sfx: 'special_bulldozer', line: 'bulldozer' },
  [SpecialId.MIRAGE]: { sfx: 'special_mirage', line: 'mirage' },
  [SpecialId.BLITZ]: { sfx: 'special_blitz', line: 'blitz' },
};

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/**
 * Classic-format events (kickoff / punt / onside tosses, halves). Matched by NAME and read loosely: the sim team
 * grows this set, and a name that is not (or no longer) emitted simply never arrives.
 */
const CLASSIC_EVENTS = [
  'KICKOFF_START',
  'KICK',
  'KICK_CAUGHT',
  'KICK_RECOVERED',
  'TOUCHBACK',
  'HALF_END',
] as const;
type ClassicEventType = (typeof CLASSIC_EVENTS)[number];
const CLASSIC_SET: ReadonlySet<string> = new Set(CLASSIC_EVENTS);
const isClassicEvent = (e: SimEvent): e is Extract<SimEvent, { type: ClassicEventType }> =>
  CLASSIC_SET.has(e.type);

interface LooseClassic {
  type: string;
  actor: number;
  kind?: string;
  downed?: boolean;
  half?: number;
}

const classicCues = (
  ev: SimEvent,
  s: SimState,
  sfx: (id: string, o?: Omit<SfxCue, 'id'>) => void,
  line: (id: string, priority: number, o?: Omit<LineCue, 'id' | 'priority'>) => void,
  crowd: (kind: CrowdReaction, strength: number) => void,
  out: CueBatch,
): void => {
  const e = ev as unknown as LooseClassic;
  switch (e.type) {
    case 'KICKOFF_START':
      // the opening kickoff is called by the bridge ("Let's play some FOOTBALL!", once per match)
      // chained: "And that's the HALF!" (4.4 s) is still talking when the second-half kickoff comes
      if (e.half === 2) line('second_half', 2, { once: true, cooldownMs: 0, chain: true });
      crowd('cheer', 0.4);
      break;
    case 'KICK': {
      const onside = e.kind === 'onside';
      // boot on leather: a punt sits a touch lower, an onside squib higher
      sfx('kick', { urgent: true, pitch: onside ? 1.12 : e.kind === 'punt' ? 0.92 : 1 });
      if (onside) line('onside_kick', 2, { cooldownMs: 0 });
      // "Here's the punt…" is a call of the moment, not colour: it takes the next phrase break of a clock call
      // ("Thirty seconds left in the half…", 3.9 s) instead of queueing behind it until it is stale
      // (and it may wait ~1.5 s for a call ahead of it: the ball is still in the air)
      else if (e.kind === 'punt') line('punt', 2, { cooldownMs: 10500, wait: 1.5 });
      else line('kick_away', 1, { chance: 0.5, cooldownMs: 30000, chatter: true });
      crowd(onside ? 'gasp' : 'ooh', onside ? 0.8 : 0.45);
      break;
    }
    case 'KICK_CAUGHT':
      sfx('catch', { urgent: true });
      crowd('cheer', 0.35);
      break;
    case 'KICK_RECOVERED':
      if (e.downed) {
        sfx('whistle', { urgent: true, pan: 0 });
        line('downed', 1, { chance: 0.6, cooldownMs: 20000, chatter: true });
      } else {
        sfx('interception', { urgent: true });
        line(e.kind === 'onside' ? 'onside_recovered' : 'recovered', 3, { cooldownMs: 0 });
        crowd('gasp', 0.9);
        out.heat.push({ player: e.actor, amount: 2 });
        out.coolOpponentOf.push(e.actor);
      }
      break;
    case 'TOUCHBACK': {
      sfx('whistle', { urgent: true, pan: 0 });
      // the returner still holds the ball in his end zone and was not tackled there: he took a knee
      const p = e.actor === 0 || e.actor === 1 ? s.players[e.actor] : null;
      const knelt = !!p && s.ball.holder === e.actor && p.state !== PlayerState.TACKLED;
      // a result: still news after a long call ahead of it (the clock warning is 3.9 s)
      line(knelt ? 'take_a_knee' : 'touchback', 1, { cooldownMs: 15000, wait: 4.2 });
      break;
    }
    case 'HALF_END':
      sfx('whistle_long', { urgent: true, pan: 0 });
      // chained: the half often ends on a scoring play whose call is still running ("Ohhh… no good!")
      if (e.half === 1) line('halftime', 3, { once: true, cooldownMs: 0, chain: true });
      crowd('cheer', 0.6);
      break;
    default:
      break;
  }
};

/** Append the cues of one event to `out`. `cameraX` drives stereo pan. */
export const cuesFor = (e: SimEvent, s: SimState, cameraX: number, out: CueBatch): void => {
  const pan = 'x' in e ? panFor(e.x, cameraX) : 0;
  const sfx = (id: string, o: Omit<SfxCue, 'id'> = {}): void => void out.sfx.push({ id, pan, ...o });
  const line = (id: string, priority: number, o: Omit<LineCue, 'id' | 'priority'> = {}): void =>
    void out.lines.push({ id, priority, ...o });
  const crowd = (kind: CrowdReaction, strength: number): void => void out.crowd.push({ kind, strength });

  switch (e.type) {
    // ── pre-snap & snap ───────────────────────────────────────────────────────────────────────────────
    case 'DRIVE_START':
      // (a new drive cools nobody: a streak is unanswered SCORES, which in Drives span several drives)
      break;
    case 'DOWN_START':
      // a real decision only (a 4th-and-long deep in his own end is a punt, not "a big play")
      if (e.down >= 4 && (e.goalToGo || e.distance <= 5 || yardsToGoal(e.los, s.rules.attackDir || 1) <= 45))
        // it may wait for the last play's result call ("SACKED!! Buried him!" is still talking 1 s later): it stays
        // news until the snap (the bridge drops it there)
        line('fourth_down', 1, { chance: 0.7, cooldownMs: 20000, chatter: true, wait: 3 });
      break;
    case 'SNAP':
      if (e.blitz) {
        sfx('whistle', { urgent: true });
        sfx('blitz_reveal', { urgent: true });
      } else sfx('snap', { urgent: true });
      // same priority as "Fourth down…": it follows that line instead of cutting it off
      if (e.playCall === PlayCall.FG_TOSS) line('fg_attempt', 1, { cooldownMs: 10000 });
      break;
    case 'HARD_COUNT':
      sfx('hard_count', { urgent: true });
      break;
    case 'OFFSIDE':
      sfx('whistle', { urgent: true });
      sfx('offside_buzzer', { urgent: true, pan: 0 });
      // a flag is news: it cuts whatever is left of the last play's call (and its caption)
      line('offside', 3, { cooldownMs: 10500 });
      crowd('boo', 0.6);
      break;
    case 'RUSH_GO':
      // An interception during the count ends it too: the pick has its own sound, no "GO!" horn.
      if (e.reason !== 'turnover') sfx('rush_go', { pan: 0 });
      break;
    case 'PLAY_CLOCK_WARNING':
      sfx('clock_tick', { pan: 0, volume: e.secondsLeft <= 1 ? 1 : 0.7 });
      break;

    // ── movement & feints ─────────────────────────────────────────────────────────────────────────────
    case 'JUMP':
      sfx(e.hurdle ? 'hurdle' : 'jump');
      break;
    case 'LAND':
      if (e.impact >= 2)
        sfx('land', { volume: clamp(e.impact / 14, 0.25, 1), pitch: e.impact > 12 ? 0.9 : 1 });
      break;
    case 'DASH':
      sfx('dash');
      break;
    case 'FAST_FALL':
      sfx('fast_fall');
      break;
    case 'FAKE_CANCEL':
      sfx('fake_cancel', { urgent: true });
      break;
    case 'STUTTER':
      sfx('stutter');
      break;
    case 'HESITATION':
      sfx('hesitation');
      break;
    case 'SPIN':
      sfx('spin');
      break;
    case 'SPIN_DODGE':
      sfx('spin_dodge', { urgent: true });
      line(e.tick % 2 ? 'no_way' : 'spin_move', 1, { chance: 0.6, cooldownMs: 12000, chatter: true });
      crowd('ooh', 0.6);
      break;
    case 'STUMBLE':
      sfx('stumble');
      break;
    case 'TRUCK_START':
      sfx('truck_start');
      break;
    case 'STIFF_ARM':
      if (e.hit) {
        sfx('stiff_arm_hit', { urgent: true });
        crowd('ooh', 0.5);
      } else sfx('stiff_arm');
      break;
    case 'DIVE_START':
      sfx('dive');
      break;
    case 'FAKE_DIVE':
      sfx('fake_dive');
      break;
    case 'LEAP_START':
      sfx('leap');
      break;
    case 'WHIFF':
      sfx('whiff');
      break;
    case 'FAKE_SUCCESS':
      line('what_a_fake', 2, { cooldownMs: 18000 });
      crowd('ooh', 0.8);
      break;
    case 'DECOY_POP':
      sfx('decoy_pop', { urgent: true });
      crowd('ooh', 0.5);
      break;
    case 'STUN':
      sfx('stun');
      break;

    // ── passing ───────────────────────────────────────────────────────────────────────────────────────
    case 'THROW_CHARGE':
      sfx('throw_charge');
      break;
    case 'THROW':
      if (e.hailMary) {
        sfx('throw_hail_mary', { urgent: true });
        crowd('ooh', 0.7);
      } else {
        const c = clamp(e.charge, 0, 1);
        sfx('throw', { volume: 0.55 + 0.45 * c, pitch: (e.lob ? 0.9 : 0.94) + 0.14 * c });
      }
      break;
    case 'PUMP_FAKE':
      sfx('pump_fake');
      break;
    case 'CATCH':
      sfx('catch', { urgent: true });
      if (e.contested || e.airYards >= 20) {
        line('what_a_catch', 2, { cooldownMs: 18000 });
        crowd('cheer', 0.8);
      } else crowd('cheer', 0.35);
      break;
    case 'INTERCEPTION':
      sfx('interception', { urgent: true });
      line('picked_off', 3);
      crowd('gasp', 0.9);
      out.heat.push({ player: e.actor, amount: 2 });
      out.coolOpponentOf.push(e.actor);
      break;
    case 'SWAT':
      sfx('swat', { urgent: true });
      line('swatted', 1, { chance: 0.5, cooldownMs: 15000, chatter: true });
      crowd('ooh', 0.6);
      break;
    case 'INCOMPLETE':
      sfx('incomplete');
      line('incomplete', 1, { chance: 0.45, cooldownMs: 12000, chatter: true });
      crowd('ooh', 0.35);
      break;
    case 'BALL_BOUNCE':
      sfx('ball_bounce', { volume: clamp(e.speed / 12, 0.15, 1), pitch: 0.9 + clamp(e.speed / 40, 0, 0.25) });
      break;
    case 'POST_HIT':
      sfx('post_hit', { urgent: true });
      line('doink', 2, { cooldownMs: 4000 });
      crowd('ooh', 0.9);
      break;

    // ── contact ───────────────────────────────────────────────────────────────────────────────────────
    case 'TACKLE': {
      const counter = e.counter !== 'none';
      sfx(counter ? 'counter_tackle' : 'tackle', {
        urgent: true,
        volume: e.kind === 'grab' ? 0.85 : 1,
        pitch: e.kind === 'leap' ? 1.06 : 1,
      });
      if (counter) {
        // counters are common between good players: the call is a highlight, not a every-play habit
        line(e.counter === 'leap_beats_hurdle' ? 'denied' : 'big_hit', 2, { chance: 0.6, cooldownMs: 30000 });
        crowd('gasp', 0.85);
      } else if (e.kind !== 'grab' && e.yards <= 0) {
        line('big_hit', 1, { chance: 0.35, cooldownMs: 15000, chatter: true });
        crowd('ooh', 0.6);
      } else crowd('ooh', 0.35);
      break;
    }
    case 'TRUCK_BREAK':
      sfx('truck_break', { urgent: true });
      line('right_through_him', 2, { cooldownMs: 18000 });
      crowd('cheer', 0.85);
      break;
    case 'HURDLE_CLEAR':
      sfx('hurdle_clear', { urgent: true });
      line('over_the_top', 2, { cooldownMs: 18000 });
      crowd('cheer', 0.75);
      break;
    case 'BULLDOZE':
      sfx('bulldoze', { urgent: true });
      line('right_through_him', 2, { cooldownMs: 18000 });
      crowd('cheer', 0.9);
      break;
    case 'HIT_STOP':
      if (
        e.cause === 'tackle' ||
        e.cause === 'counter' ||
        e.cause === 'truck' ||
        e.cause === 'bulldoze' ||
        e.cause === 'stiff_arm' ||
        e.cause === 'interception'
      )
        sfx('hitstop', { urgent: true, volume: clamp(e.frames / 8, 0.5, 1) });
      break;

    // ── results & scoring ─────────────────────────────────────────────────────────────────────────────
    case 'PLAY_END':
      switch (e.result) {
        case PlayResultKind.TACKLE:
        case PlayResultKind.INCOMPLETE:
        case PlayResultKind.INTERCEPTION:
        case PlayResultKind.SACK:
        case PlayResultKind.TURNOVER_ON_DOWNS:
        case PlayResultKind.CONVERSION_FAILED:
          sfx('whistle_long', { urgent: true, pan: 0, volume: 0.8 });
          break;
        default:
          break;
      }
      break;
    case 'FIRST_DOWN':
      sfx('first_down', { urgent: true, pan: 0 });
      line('first_down', 1, { chance: 0.55, cooldownMs: 15000 });
      crowd('cheer', 0.5);
      break;
    case 'BIG_PLAY':
      line('look_at_him_go', 1, { cooldownMs: 15000 });
      crowd('cheer', 0.8);
      break;
    case 'TOUCHDOWN':
      sfx('td_fanfare', { urgent: true, pan: 0 });
      sfx('air_horn', { urgent: true, pan: 0, volume: 0.6 });
      // the long call ("He's at the forty… the twenty…") narrates the instant replay of a long score (bridge)
      line(e.pickSix ? 'pick_six' : e.kickReturn ? 'return_td' : 'touchdown', 4, { cooldownMs: 0 });
      crowd('roar', 1);
      out.heat.push({ player: e.actor, amount: 2 });
      out.coolOpponentOf.push(e.actor);
      break;
    case 'FIELD_GOAL_GOOD':
      sfx(e.pat ? 'conversion_good' : 'fg_good', { urgent: true, pan: 0 });
      line(e.pat ? 'pat_good' : e.distance >= LONG_FG_YARDS ? 'its_good_long' : 'its_good', 3, {
        cooldownMs: 0,
      });
      crowd('cheer', 0.9);
      if (!e.pat) {
        out.heat.push({ player: e.actor, amount: 1 });
        out.coolOpponentOf.push(e.actor);
      }
      break;
    case 'FIELD_GOAL_MISS':
      sfx('fg_miss', { urgent: true, pan: 0 });
      line('no_good', 3, { cooldownMs: 0 });
      crowd(e.reason === 'post' ? 'ooh' : 'boo', 0.75);
      break;
    case 'SAFETY':
      sfx('safety', { urgent: true, pan: 0 });
      line('safety', 3, { cooldownMs: 0 });
      crowd('roar', 0.85);
      out.heat.push({ player: e.actor, amount: 2 });
      out.coolOpponentOf.push(e.actor);
      break;
    case 'SACK':
      sfx('sack', { urgent: true });
      // the full call at most every ~25 s (the crowd and the sack stinger carry the ones in between)
      line('sacked', 3, { cooldownMs: 25000 });
      crowd('roar', 0.75);
      break;
    case 'TURNOVER_ON_DOWNS':
      sfx('turnover', { urgent: true, pan: 0 });
      line('turnover_on_downs', 2, { cooldownMs: 0, follow: true });
      crowd('boo', 0.7);
      break;
    case 'CONVERSION_START':
      break;
    case 'CONVERSION_CHOICE':
      if (e.choice === 'two_point') line('two_point', 2, { cooldownMs: 0 });
      break;
    case 'CONVERSION_RESULT':
      sfx(e.good ? 'conversion_good' : 'conversion_fail', { urgent: true, pan: 0 });
      // the 1-pt toss has its own FIELD_GOAL_GOOD/MISS call; the 2-pt play is called here — and so is a toss that
      // never went up (the kicker was tackled before letting go: no FIELD_GOAL_MISS, the screen says NO GOOD!)
      if (s.rules.conversion === ConversionChoice.TWO_POINT)
        line(e.good ? 'two_good' : 'two_stopped', 3, { cooldownMs: 0 });
      else if (!e.good && !out.lines.some((l) => l.priority >= 3)) line('no_good', 3, { cooldownMs: 0 });
      if (e.good) crowd('cheer', 0.8);
      else crowd('boo', 0.5);
      break;
    case 'OVERTIME_START':
      sfx('overtime', { urgent: true, pan: 0 });
      line('overtime', 3, { cooldownMs: 0 });
      crowd('roar', 0.6);
      break;
    case 'GAME_OVER':
      sfx('final_horn', { urgent: true, pan: 0 });
      // chained: the scoring call still running finishes first, and the line can never expire behind it
      line('ballgame', 4, { cooldownMs: 0, chain: true });
      crowd('roar', 1);
      break;
    case 'CELEBRATE':
      sfx('celebrate', { volume: 0.8 });
      break;

    // ── resources & specials ──────────────────────────────────────────────────────────────────────────
    case 'STAMINA_EMPTY':
      sfx('stamina_empty', { pan: 0 });
      line('gassed', 1, { chance: 0.35, cooldownMs: 90000, chatter: true });
      break;
    case 'STAMINA_DENIED':
      sfx('stamina_denied', { pan: 0 });
      break;
    case 'POWER_FULL': {
      sfx('power_full', { pan: 0, urgent: true });
      // "Big Tom is all charged up… BULLDOZER time!" (the streak call "he's heating up" is the bridge's)
      const p = e.actor === 0 || e.actor === 1 ? s.players[e.actor] : null;
      const ready = p ? READY_LINES[CHARACTER_IDS[p.character] ?? ''] : undefined;
      // long reads (4-4.6 s): once in a while, not every time the meter fills — and never mid-play: the meter often
      // fills from a catch or a broken tackle on the way to a score, and the TOUCHDOWN call cut it on its first word
      if (ready) line(ready, 1, { cooldownMs: 90000, deadBall: true });
      break;
    }
    case 'POWER_GAIN':
      if (e.amount >= 10) sfx('power_gain', { pan: 0, volume: 0.7 });
      break;
    case 'SPECIAL_TELEGRAPH':
      sfx('special_telegraph', { urgent: true });
      crowd('ooh', 0.6);
      break;
    case 'SPECIAL_ACTIVATE': {
      const sp = SPECIAL_LINES[e.special];
      if (sp) {
        sfx(sp.sfx, { urgent: true });
        // the hype call sits just under the play's results: "BLITZ!! — SACKED!!" (the sack 0.2 s later takes the
        // call's phrase break instead of expiring behind "Here comes Grandpa!")
        line(sp.line, SPECIAL_PRIORITY, { cooldownMs: 0 });
      }
      break;
    }
    case 'SPECIAL_END':
      break;

    default: {
      if (isClassicEvent(e)) {
        classicCues(e, s, sfx, line, crowd, out);
        break;
      }
      // Exhaustiveness: a new SimEvent type must be mapped (or explicitly ignored) here.
      const never: never = e;
      void never;
    }
  }
};
